import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SidebarProvider } from "@/ui/components/primitives/sidebar";
import { AppSidebar } from "@/ui/components/layout/sidebar/app-sidebar";
import { TopBar } from "@/ui/components/layout/header/top-bar";
import { MobileNav } from "@/ui/components/layout/mobile/mobile-nav";
import { DesignSystemInfo } from "@/modules/shared/components/design-system-info";
import { ThemeProvider } from "@/app/providers/theme-provider";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute, MerchantOnlyRoute } from "@/access-control/core/permission";
import { LoginPage } from "@/modules/auth/pages/login-page";
import { DashboardPage } from "@/modules/dashboard/pages/dashboard-page";
import { TransactionsPage } from "@/modules/transactions/pages/transactions-page";
import { TransactionDetailPage } from "@/modules/transactions/pages/transaction-detail-page";
import { CreatePayinPage } from "@/modules/payments/pages/create-payin-page";
import { CreatePayoutPage } from "@/modules/payments/pages/create-payout-page";
import { WalletPage } from "@/modules/wallet/pages/wallet-page";
import { ApiKeysPage } from "@/modules/settings/pages/api-keys-page";
import { SettingsPage } from "@/modules/settings/pages/settings-page";
import { ApprovalsPage } from "@/modules/approvals/pages/approvals-page";
import PSPManagementPage from "@/modules/admin/pages/psp-management-page";
import { Toaster } from "@/ui/components/primitives/sonner";
import { ErrorBoundary } from "@/ui/components/composed/error-boundary";
import { RouteErrorBoundary } from "@/ui/components/composed/route-error-boundary";
import { AsyncErrorBoundary } from "@/ui/components/composed/async-error-boundary";

function AppContent() {
  const { pathname } = useLocation();
  const { isAuthenticated, isInitialized } = useAuth();

  // Show loading screen while auth is being initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isLogin = pathname === "/login";

  return (
    <div className="min-h-screen bg-background">
      <ErrorBoundary>
        <AsyncErrorBoundary>
          <ThemeProvider defaultTheme="system" storageKey="paygate-theme">
            {!isAuthenticated && !isLogin ? (
              // Not authenticated and not on login page, redirect to login
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            ) : isLogin ? (
                <ErrorBoundary fallback={
                  <div className="min-h-screen bg-background flex items-center justify-center">
                    <div>PayGate authentication error. Please refresh the page.</div>
                  </div>
                }>
                  <div className="min-h-screen bg-background">
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="*" element={<Navigate to="/login" replace />} />
                    </Routes>
                  </div>
                </ErrorBoundary>
              ) : (
                <SidebarProvider>
                  <div className="flex min-h-screen w-full bg-background">
                    <AppSidebar />
                    <div className="flex flex-1 flex-col min-w-0">
                      <TopBar />
                      <main
                        className="flex-1 overflow-y-auto overflow-x-hidden pb-20 md:pb-0 min-w-0"
                        style={{
                          maxWidth: '1200px',
                          width: '100%',
                          marginLeft: 'auto',
                          marginRight: 'auto'
                        }}
                      >
                        <ErrorBoundary fallback={
                          <div className="p-4">
                            <div>PayGate application error. Please try refreshing the page.</div>
                          </div>
                        }>
                          <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={
                              <ProtectedRoute>
                                <ErrorBoundary>
                                  <DashboardPage />
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } />
                            <Route path="/transactions" element={
                              <ProtectedRoute>
                                <ErrorBoundary>
                                  <TransactionsPage />
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } />
                            <Route path="/transactions/:id" element={
                              <ProtectedRoute>
                                <ErrorBoundary>
                                  <TransactionDetailPage />
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } />
                            <Route path="/create-payin" element={
                              <ProtectedRoute>
                                <ErrorBoundary>
                                  <CreatePayinPage onBack={() => history.back()} />
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } />
                            <Route path="/create-payout" element={
                              <ProtectedRoute>
                                <ErrorBoundary>
                                  <CreatePayoutPage onBack={() => history.back()} />
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } />
                            <Route path="/wallet" element={
                              <ProtectedRoute>
                                <MerchantOnlyRoute>
                                  <ErrorBoundary>
                                    <WalletPage />
                                  </ErrorBoundary>
                                </MerchantOnlyRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/api-keys" element={
                              <ProtectedRoute>
                                <MerchantOnlyRoute>
                                  <ErrorBoundary>
                                    <ApiKeysPage />
                                  </ErrorBoundary>
                                </MerchantOnlyRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/approvals" element={
                              <ProtectedRoute>
                                <ErrorBoundary>
                                  <ApprovalsPage />
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } />
                            <Route path="/settings" element={
                              <ProtectedRoute>
                                <ErrorBoundary>
                                  <SettingsPage />
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } />
                            <Route path="/admin/psp-management" element={
                              <ProtectedRoute>
                                <ErrorBoundary>
                                  <PSPManagementPage />
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } />

                            {/* Catch-all route with error boundary */}
                            <Route path="*" element={<RouteErrorBoundary />} />
                          </Routes>
                        </ErrorBoundary>
                      </main>
                    </div>
                    <MobileNav />
                    <DesignSystemInfo />
                  </div>
                </SidebarProvider>
              )}
              <Toaster />
            </ThemeProvider>
          </AsyncErrorBoundary>
      </ErrorBoundary>
    </div>
  );
}

// Main App component that wraps everything with AuthProvider
export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}